'use client'

import { CheckCircle2, Loader2 } from 'lucide-react'
import { AnimatePresence, motion } from 'motion/react'
import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'

interface SecondBentoAnimationProps {
  isHovered?: boolean
}

export function SecondBentoAnimation({
  isHovered = false,
}: SecondBentoAnimationProps) {
  const ref = useRef(null)
  const [elapsedMs, setElapsedMs] = useState(0)

  useEffect(() => {
    let animationFrameId = 0

    if (!isHovered) {
      animationFrameId = requestAnimationFrame(() => {
        setElapsedMs(0)
      })
      return () => cancelAnimationFrame(animationFrameId)
    }

    const startTime = performance.now()
    const tick = (currentTime: number) => {
      setElapsedMs(currentTime - startTime)
      animationFrameId = requestAnimationFrame(tick)
    }

    animationFrameId = requestAnimationFrame(tick)

    return () => {
      cancelAnimationFrame(animationFrameId)
    }
  }, [isHovered])

  const shouldAnimate = isHovered && elapsedMs >= 2000
  const isToolCallVisible = isHovered && elapsedMs >= 2000
  const isEditPromptVisible = true
  const hasTransformedToOpenAI = isHovered && elapsedMs >= 4500
  const isEditComplete = isHovered && elapsedMs >= 4500
  const isThirdMessageVisible = isHovered && elapsedMs >= 5500
  const isThirdResponseVisible = isHovered && elapsedMs >= 7000
  const isThirdEditComplete = isHovered && elapsedMs >= 9500
  const hasExpandedText = isHovered && elapsedMs >= 9500

  return (
    <div
      ref={ref}
      className="flex h-full w-full items-center justify-center px-6 pt-6"
    >
      <motion.div
        className="flex h-full w-full max-w-5xl items-start gap-20"
        transition={{
          type: 'spring',
          stiffness: 300,
          damping: 20,
        }}
      >
        {/* Left Side - Chat */}
        <div className="flex flex-1 flex-col gap-6 pt-4">
          {/* Previous Context - First User Message */}
          <div className="flex items-end justify-end gap-2 opacity-40">
            <div className="max-w-[280px] rounded-xl rounded-br-sm bg-white px-3 py-2 text-zinc-900 shadow-sm dark:bg-white">
              <p className="text-sm leading-relaxed">
                Create an email announcing the Claude 3 models
              </p>
            </div>
            <div className="flex w-fit shrink-0 items-center rounded-full border border-zinc-200 bg-white dark:border-zinc-700">
              <Image
                src="https://randomuser.me/api/portraits/women/79.jpg"
                alt="User Avatar"
                className="size-8 shrink-0 rounded-full"
                width={32}
                height={32}
                sizes="32px"
                quality={60}
              />
            </div>
          </div>

          {/* Previous Context - First Brew Response */}
          <div className="flex items-end gap-2 opacity-40">
            <div className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full">
              <Image
                src="/images/circle_paper.png"
                alt="Brew Logo"
                width={32}
                height={32}
                className="size-8"
              />
            </div>
            <div className="max-w-[220px] flex-1">
              <div className="flex min-h-[48px] items-center gap-2 rounded-xl rounded-bl-sm border border-green-200 bg-green-50 px-3 py-2 shadow-sm">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="font-medium text-green-700 text-sm">
                  Email generated
                </span>
              </div>
            </div>
          </div>

          {/* Second User Message - Right Aligned */}
          <div className="flex items-end justify-end gap-2">
            <motion.div
              className="max-w-[280px] rounded-xl rounded-br-sm bg-white px-3 py-2 text-zinc-900 shadow-sm dark:bg-white"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.8,
                ease: 'easeOut',
                delay: 0.5,
              }}
            >
              <p className="text-sm leading-relaxed">
                Update the branding to be OpenAI
              </p>
            </motion.div>
            <motion.div
              className="flex w-fit shrink-0 items-center rounded-full border border-zinc-200 bg-white dark:border-zinc-700"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{
                duration: 0.5,
                ease: 'easeOut',
                delay: 0.5,
              }}
            >
              <Image
                src="https://randomuser.me/api/portraits/women/79.jpg"
                alt="User Avatar"
                className="size-8 shrink-0 rounded-full"
                width={32}
                height={32}
                sizes="32px"
                quality={60}
              />
            </motion.div>
          </div>

          {/* Brew Edit Response - Left Aligned */}
          <div className="flex items-end gap-2">
            {shouldAnimate ? (
              <motion.div
                className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full shadow-sm"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.5,
                  ease: 'easeOut',
                }}
              >
                <Image
                  src="/images/circle_paper.png"
                  alt="Brew Logo"
                  width={32}
                  height={32}
                  className="size-8"
                />
              </motion.div>
            ) : null}

            <div className="max-w-[220px] flex-1">
              <AnimatePresence mode="wait">
                {shouldAnimate && isToolCallVisible ? (
                  <motion.div
                    key="tool-call"
                    className={`${
                      isEditComplete
                        ? 'border-green-200 bg-green-50'
                        : 'border-white/60 bg-white/80 backdrop-blur-sm'
                    } flex min-h-[48px] items-center gap-2 rounded-xl rounded-bl-sm border px-3 py-2 shadow-sm transition-colors duration-500`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.8,
                      ease: 'easeOut',
                    }}
                  >
                    <motion.div
                      className="relative flex h-4 w-4 shrink-0 items-center justify-center"
                      animate={isEditComplete ? { scale: [1, 1.2, 1] } : {}}
                      transition={{ duration: 0.5 }}
                    >
                      {isEditComplete ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      )}
                    </motion.div>
                    <span
                      className={`${
                        isEditComplete
                          ? 'font-medium text-green-700'
                          : 'text-zinc-700'
                      } flex-1 text-sm transition-colors duration-500`}
                    >
                      {isEditComplete ? 'Email updated' : 'Updating...'}
                    </span>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          </div>

          {/* Third User Message - Right Aligned */}
          {isThirdMessageVisible ? (
            <div className="flex items-end justify-end gap-2">
              <motion.div
                className="max-w-[280px] rounded-xl rounded-br-sm bg-white px-3 py-2 text-zinc-900 shadow-sm dark:bg-white"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.8,
                  ease: 'easeOut',
                }}
              >
                <p className="text-sm leading-relaxed">Make it personal</p>
              </motion.div>
              <motion.div
                className="flex w-fit shrink-0 items-center rounded-full border border-zinc-200 bg-white dark:border-zinc-700"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.5,
                  ease: 'easeOut',
                }}
              >
                <Image
                  src="https://randomuser.me/api/portraits/women/79.jpg"
                  alt="User Avatar"
                  className="size-8 shrink-0 rounded-full"
                  width={32}
                  height={32}
                  sizes="32px"
                  quality={60}
                />
              </motion.div>
            </div>
          ) : null}

          {/* Third Brew Response - Left Aligned */}
          {isThirdResponseVisible ? (
            <div className="flex items-end gap-2">
              <motion.div
                className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full shadow-sm"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{
                  duration: 0.5,
                  ease: 'easeOut',
                }}
              >
                <Image
                  src="/images/circle_paper.png"
                  alt="Brew Logo"
                  width={32}
                  height={32}
                  className="size-8"
                />
              </motion.div>

              <div className="max-w-[220px] flex-1">
                <AnimatePresence mode="wait">
                  <motion.div
                    key="third-response"
                    className={`${
                      isThirdEditComplete
                        ? 'border-green-200 bg-green-50'
                        : 'border-white/60 bg-white/80 backdrop-blur-sm'
                    } flex min-h-[48px] items-center gap-2 rounded-xl rounded-bl-sm border px-3 py-2 shadow-sm transition-colors duration-500`}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{
                      duration: 0.8,
                      ease: 'easeOut',
                    }}
                  >
                    <motion.div
                      className="relative flex h-4 w-4 shrink-0 items-center justify-center"
                      animate={
                        isThirdEditComplete ? { scale: [1, 1.2, 1] } : {}
                      }
                      transition={{ duration: 0.5 }}
                    >
                      {isThirdEditComplete ? (
                        <CheckCircle2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      )}
                    </motion.div>
                    <span
                      className={`${
                        isThirdEditComplete
                          ? 'font-medium text-green-700'
                          : 'text-zinc-700'
                      } flex-1 text-sm transition-colors duration-500`}
                    >
                      {isThirdEditComplete ? 'Email updated' : 'Updating...'}
                    </span>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          ) : null}
        </div>

        {/* Right Side - Email Preview */}
        <div className="flex h-full flex-1 items-center justify-center">
          <AnimatePresence>
            {isEditPromptVisible && (
              <motion.div
                className="mb-[-100px] min-w-[340px] rounded-t-xl bg-[#F0EFEA] shadow-xl md:min-w-[420px]"
                initial={{ opacity: 1, scale: 1, y: 0 }}
                animate={{
                  opacity: 1,
                  scale: 1,
                  y: 0,
                }}
                exit={{
                  opacity: 0,
                  scale: 0.98,
                  y: 40,
                  transition: {
                    duration: 0.8,
                    ease: [0.32, 0, 0.67, 0],
                  },
                }}
              >
                {!hasTransformedToOpenAI ? (
                  // Anthropic Email (from original HTML)
                  <>
                    <div className="mx-2 mt-2 overflow-hidden rounded-lg bg-white">
                      <div className="flex items-center justify-between px-5 pt-5 pb-4">
                        <Image
                          src="https://cdn.brew.new/marketing/anthropic-wordmark-dark.png"
                          alt="Anthropic"
                          className="h-3"
                          width={160}
                          height={18}
                          sizes="160px"
                          quality={70}
                        />
                        <div className="rounded bg-[#F0EFEA] px-3 py-1.5 font-bold text-[#141413] text-[10px]">
                          Visit Website
                        </div>
                      </div>
                      <div className="px-5 pb-4">
                        <h1 className="mb-3 font-bold text-2xl text-[#141413] leading-tight">
                          Introducing Claude 3
                        </h1>
                        <p className="mb-4 text-[#828179] text-xs leading-relaxed">
                          Our most capable AI model family yet. Experience
                          unprecedented intelligence, safety, and reliability
                          across three powerful tiers: Opus, Sonnet, and Haiku.
                        </p>
                        <button
                          type="button"
                          className="rounded bg-[#CC785C] px-5 py-2.5 font-bold text-white text-xs"
                        >
                          Explore Claude 3
                        </button>
                      </div>
                      <Image
                        src="https://cdn.brew.new/marketing/anthropic-claude3-hero.jpeg"
                        alt="Claude 3"
                        className="h-20 w-full object-cover"
                        width={600}
                        height={160}
                        sizes="(max-width: 768px) 100vw, 420px"
                        quality={75}
                      />
                    </div>

                    {/* Three Models Section */}
                    <div className="mx-2 mt-2 overflow-hidden rounded-lg bg-white">
                      <div className="px-5 pt-4 pb-2">
                        <h2 className="mb-3 font-bold text-[#141413] text-base">
                          Three models, one mission
                        </h2>
                        <div className="mb-4 space-y-3">
                          <div className="flex gap-2">
                            <Image
                              src="https://placehold.co/64x64/CC785C/ffffff?text=Opus"
                              alt="Opus"
                              className="h-16 w-16 shrink-0 rounded object-cover"
                              width={64}
                              height={64}
                              sizes="64px"
                              quality={65}
                            />
                            <div>
                              <h3 className="mb-0.5 font-bold text-[#141413] text-xs">
                                Claude 3 Opus
                              </h3>
                              <p className="text-[#828179] text-[10px] leading-snug">
                                Our most intelligent model for complex analysis
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Image
                              src="https://placehold.co/64x64/828179/ffffff?text=Sonnet"
                              alt="Sonnet"
                              className="h-16 w-16 shrink-0 rounded object-cover"
                              width={64}
                              height={64}
                              sizes="64px"
                              quality={65}
                            />
                            <div>
                              <h3 className="mb-0.5 font-bold text-[#141413] text-xs">
                                Claude 3 Sonnet
                              </h3>
                              <p className="text-[#828179] text-[10px] leading-snug">
                                Balance of intelligence and speed
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Image
                              src="https://placehold.co/64x64/F0EFEA/141413?text=Haiku"
                              alt="Haiku"
                              className="h-16 w-16 shrink-0 rounded object-cover"
                              width={64}
                              height={64}
                              sizes="64px"
                              quality={65}
                            />
                            <div>
                              <h3 className="mb-0.5 font-bold text-[#141413] text-xs">
                                Claude 3 Haiku
                              </h3>
                              <p className="text-[#828179] text-[10px] leading-snug">
                                Fast and efficient for everyday tasks
                              </p>
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          className="w-full rounded bg-[#F0EFEA] py-2 font-bold text-[#141413] text-xs"
                        >
                          Compare Models
                        </button>
                      </div>
                    </div>
                  </>
                ) : (
                  // OpenAI Email (from openai-generate.html)
                  <>
                    <div className="mx-2 mt-2 overflow-hidden rounded-lg bg-white">
                      <div className="flex items-center justify-between px-5 pt-5 pb-4">
                        <Image
                          src="https://cdn.brew.new/marketing/openai-wordmark-dark.png"
                          alt="OpenAI"
                          className="h-3"
                          width={160}
                          height={18}
                          sizes="160px"
                          quality={70}
                        />
                        <div className="rounded bg-[#080808] px-3 py-1.5 font-bold text-[10px] text-white">
                          Visit OpenAI
                        </div>
                      </div>
                      <div className="px-5 pb-4">
                        <h1 className="mb-3 font-bold text-2xl text-[#080808] leading-tight">
                          GPT-5.1 is here
                        </h1>
                        <p className="mb-4 text-[#080808] text-xs leading-relaxed">
                          The most advanced AI system we&apos;ve ever built.
                          GPT-5.1 understands context like never before and
                          generates human-quality responses.
                        </p>
                        <button
                          type="button"
                          className="rounded bg-[#080808] px-5 py-2.5 font-bold text-white text-xs"
                        >
                          Experience GPT-5.1
                        </button>
                      </div>
                      <Image
                        src="https://cdn.brew.new/marketing/openai-gpt5-hero.jpeg"
                        alt="GPT-5"
                        className="h-20 w-full object-cover"
                        width={600}
                        height={160}
                        sizes="(max-width: 768px) 100vw, 420px"
                        quality={75}
                      />
                    </div>

                    {/* Personal Message or Breakthrough Capabilities Section */}
                    <div className="mx-2 mt-2 overflow-hidden rounded-lg bg-white">
                      <div className="px-5 pt-4 pb-2">
                        {!hasExpandedText ? (
                          <>
                            <h2 className="mb-3 font-bold text-[#080808] text-lg">
                              Breakthrough capabilities
                            </h2>
                            <div className="mb-4 space-y-3">
                              <div className="flex gap-2">
                                <Image
                                  src="https://placehold.co/64x64/080808/FFFFFF?text=Advanced+Reasoning"
                                  alt="Advanced Reasoning"
                                  className="h-16 w-16 shrink-0 rounded-lg object-cover"
                                  width={64}
                                  height={64}
                                  sizes="64px"
                                  quality={65}
                                />
                                <div>
                                  <h3 className="mb-0.5 font-bold text-[#080808] text-xs">
                                    Advanced reasoning
                                  </h3>
                                  <p className="text-[#080808] text-[10px] leading-snug">
                                    Thinks through multi-step problems with
                                    unprecedented logic
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Image
                                  src="https://placehold.co/64x64/080808/FFFFFF?text=Multimodal+AI"
                                  alt="Multimodal"
                                  className="h-16 w-16 shrink-0 rounded-lg object-cover"
                                  width={64}
                                  height={64}
                                  sizes="64px"
                                  quality={65}
                                />
                                <div>
                                  <h3 className="mb-0.5 font-bold text-[#080808] text-xs">
                                    True multimodal understanding
                                  </h3>
                                  <p className="text-[#080808] text-[10px] leading-snug">
                                    Process and generate text, images, audio,
                                    and video
                                  </p>
                                </div>
                              </div>
                              <div className="flex gap-2">
                                <Image
                                  src="https://placehold.co/64x64/080808/FFFFFF?text=Safety+First"
                                  alt="Safety"
                                  className="h-16 w-16 shrink-0 rounded-lg object-cover"
                                  width={64}
                                  height={64}
                                  sizes="64px"
                                  quality={65}
                                />
                                <div>
                                  <h3 className="mb-0.5 font-bold text-[#080808] text-xs">
                                    Built-in safety systems
                                  </h3>
                                  <p className="text-[#080808] text-[10px] leading-snug">
                                    Advanced safety mechanisms for responsible
                                    deployment
                                  </p>
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="mb-4">
                            <div className="mb-4 flex items-center gap-3 border-[#080808]/10 border-b pb-3">
                              <Image
                                src="/people/Sam-Altman.png"
                                alt="Sam Altman"
                                className="h-16 w-16 shrink-0 rounded-full object-cover"
                                width={64}
                                height={64}
                                sizes="64px"
                                quality={70}
                              />
                              <div>
                                <p className="font-bold text-[#080808] text-sm">
                                  Sam Altman
                                </p>
                                <p className="text-[#080808]/60 text-xs">
                                  CEO, OpenAI
                                </p>
                              </div>
                            </div>
                            <div className="space-y-2.5">
                              <p className="text-[#080808] text-xs leading-relaxed">
                                GPT-5.1 is out! It&apos;s a nice upgrade.
                              </p>
                              <p className="text-[#080808] text-xs leading-relaxed">
                                I particularly like the improvements in
                                instruction following, and the adaptive
                                thinking. The intelligence and style
                                improvements are good too.
                              </p>
                              <p className="text-[#080808] text-xs leading-relaxed">
                                Also, we&apos;ve made it easier to customize
                                ChatGPT. You can pick from presets (Default,
                                Friendly, Efficient, Professional, Candid, or
                                Quirky) or tune it yourself.
                              </p>
                            </div>
                          </div>
                        )}
                        <button
                          type="button"
                          className="w-full rounded bg-[#080808] py-2 font-bold text-white text-xs"
                        >
                          {hasExpandedText ? 'Learn More' : 'Read the Research'}
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  )
}
